// pollyfills
import "core-js/stable";
import "regenerator-runtime/runtime";
import 'promise-polyfill/src/polyfill';
import 'isomorphic-unfetch';
import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/dist/locale-data/en';
import 'focus-within-polyfill';
import '@okta/okta-auth-js/polyfill';

import 'react-dates/initialize'

import React, { useMemo } from "react";
import ReactDOM from 'react-dom';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { ApolloClient, ApolloProvider, ApolloLink, createHttpLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import apolloLogger from 'apollo-link-logger';
import ApolloLinkTimeout from 'apollo-link-timeout';
import DebounceLink from 'apollo-link-debounce';
import { InMemoryCache, StoreObject } from '@apollo/client/cache';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';

import { Security, useOktaAuth } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import AppErrorBoundary from 'components/AppErrorBoundary/AppErrorBoundary';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { ApolloServerErrorCode } from 'components/ErrorScreens/CodedError';

import 'assets/styles/main.global.scss';

import BaseRoutes from "components/App";
import { ROUTES } from 'constants/routeConstants';
import { hasAccessTokenExpired } from "utils/authUtils";

if (process.env.DEV_MODE) {
  // eslint-disable-next-line
  // @ts-ignore
  __webpack_require__.p = 'https://127.0.0.1:3000/'; //eslint-disable-line
}

const DEFAULT_DEBOUNCE_TIMEOUT = 100;

const oktaConfig = {
  clientId: process.env.OKTA_CLIENT_ID,
  issuer: process.env.OKTA_ISSUER,
  redirectUri: process.env.OKTA_REDIRECT_URI,
  scopes: ['openid', 'profile', 'email'],
  pkce: true
  // disableHttpsCheck: process.env.OKTA_OKTA_TESTING_DISABLEHTTPSCHECK
};
const oktaAuthService = new OktaAuth(oktaConfig);

const ApolloWrapper = ({ children }: { children: React.ReactNode }) => {
  const { oktaAuth } = useOktaAuth();
  const navigate = useNavigate()

  const renewToken = async () => {
    try {
      // Use getWithoutPrompt to fetch a new access token
      const tokenResponse = await oktaAuth.token.getWithoutPrompt({ ...oktaConfig });

      // Store the new tokens in the token manager
      oktaAuth.tokenManager.setTokens({
        accessToken: tokenResponse.tokens.accessToken,
        idToken: tokenResponse.tokens.idToken
      });
      return tokenResponse.tokens.accessToken.accessToken
    } catch (error) {
      console.error('Error during silent token renewal:', error);
      oktaAuth.signInWithRedirect();
    }
  };

  const client = useMemo(() => {
    const refreshIfAuthError = (code: string) => {
      if (code === ApolloServerErrorCode.UNAUTHENTICATED) {
        navigate(`${ROUTES.codedError}?code=${ApolloServerErrorCode.UNAUTHENTICATED}`);
        oktaAuth.signInWithRedirect();
      }
    };
    const timeoutLink = new ApolloLinkTimeout(10000); // 10 second timeout

    const authLink = setContext((_, { headers }) => {
      const oktaApiToken = localStorage.getItem('okta-api-storage');
      let oktaToken = null;

      if (oktaApiToken) {
        oktaToken = JSON.parse(oktaApiToken).token;
      }

      const token = oktaAuth.getAccessToken();

      if (!token) {
        oktaAuth.signInWithRedirect();
      }

      if (hasAccessTokenExpired(token)) {
        return renewToken()
          .then((accessToken) => ({
            headers: {
              ...headers,
              authorization: accessToken ? `Bearer ${accessToken}` : '',
              'okta-auth': oktaToken
            }
          }))
      }

      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : '',
          'okta-auth': oktaToken
        }
      }
    });

    const link = ApolloLink.from([
      onError(({ graphQLErrors, networkError}) => {
        if (graphQLErrors) {
          graphQLErrors.forEach(({ locations, message, path, extensions }) => {
            // eslint-disable-next-line no-console
            console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

            if (extensions?.code) {
              refreshIfAuthError(extensions?.code);
            }
          });
        }
        if (networkError) {
          // eslint-disable-next-line no-console
          console.error(`[Network error]: ${networkError}`);
          //@ts-expect-error statusCode does exist
          if (networkError.statusCode === 400) {
            oktaAuth.session.refresh().catch((e: Error) => {
              console.error(`[Refresh error]: ${e.message}`);
              oktaAuth.signInWithRedirect();
            });
          }
        }
      }),
      apolloLogger,
      new DebounceLink(DEFAULT_DEBOUNCE_TIMEOUT),
      ApolloLink.split(
        (operation) => operation.getContext().isFederated,
        createHttpLink({
          uri: process.env.SUPERGRAPH_URI
        }),
        ApolloLink.split(
          (operation) => operation.getContext().canBatch,
          new BatchHttpLink({
            uri: process.env.GRAPHQL_URI,
            credentials: 'same-origin'
          }),
          createHttpLink({
            uri: process.env.GRAPHQL_URI,
            credentials: 'same-origin'
          })
        )
      )
    ]);
    const links = authLink.concat(link).concat(timeoutLink);

    const normalizeKeyField = (keyField: string) => {
      return (obj: Readonly<StoreObject>) => {
        if(Object.prototype.hasOwnProperty.call(obj, 'id')){
          return ['id']
        }else if(Object.prototype.hasOwnProperty.call(obj, keyField)){
          return [keyField]
        }
      }
    }

    return new ApolloClient({
      link: links,
      cache: new InMemoryCache({
        typePolicies: {
          Load: {
            keyFields: normalizeKeyField('LoadBoardId')
          },
          Offer: {
            keyFields: ['id']
          },
          FacilityDetail: {
            keyFields: normalizeKeyField('FacilityId')
          },
          LoadStop: {
            keyFields: normalizeKeyField('LoadStopId')
          },
          User: {
            keyFields: normalizeKeyField('UserId')
          },
          CarrierRepType: {
            keyFields: normalizeKeyField('UserId')
          },
          Tender: {
            keyFields: normalizeKeyField('LoadNumber')
          },
          Match: {
            keyFields: normalizeKeyField('LoadBoardId')
          }
        }
      })
    });
  }, [oktaAuth]); // eslint-disable-line

  return (
    <ApolloProvider client={client}>
      {children}
    </ApolloProvider>
  )
}

const SecurityWrapper = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate()
  const restoreOriginalUri = (_oktaAuth: any, originalUri: string) => {
    if (navigate) {
      navigate(toRelativeUrl(originalUri, window.location.origin), {replace: true});
    }
  };

  return (
    <Security
      oktaAuth={oktaAuthService}
      restoreOriginalUri={restoreOriginalUri}
    >
      {children}
    </Security>
  )
}

const App = () => {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <SecurityWrapper>
          <ApolloWrapper>
            <BaseRoutes />
          </ApolloWrapper>
        </SecurityWrapper>
      </BrowserRouter>
    </AppErrorBoundary>
  )
};


(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: process.env.LAUNCH_DARKLY_CLIENT_ID,
    reactOptions: {
      useCamelCaseFlagKeys: false
    },
    context: {
      kind: 'user',
      anonymous: true
    }
  });

  // eslint-disable-next-line
  ReactDOM.render(
    <LDProvider>
      <App />
    </LDProvider>,
    document.getElementById("root")
  );
})();

export default App;
